/**
 * A tenant's stored theme, resolved and sanitized into CSS custom properties.
 *
 * Security-critical, and the guarantee is structural rather than filter-based:
 * the color loop iterates `STOREFRONT_TOKEN_KEYS`, never the stored object, so a
 * tenant can never introduce a *variable name* — only supply a value for one of
 * ours, and a rejected value falls back to the palette default rather than
 * leaving the variable blank. Keep it that way; switching to
 * `Object.entries(theme.tokens)` would hand tenants the variable namespace.
 *
 * DOM-free on purpose. The storefront writes the result onto
 * `document.documentElement` for the whole page; the dashboard's box preview
 * spreads it into one container's `style` to scope a tenant theme to a panel.
 * Neither concern belongs in the sanitizer.
 */
import type { StorefrontTheme } from "./index.js";
import {
  resolveStorefrontTokens,
  STOREFRONT_TOKEN_DEFAULTS,
  STOREFRONT_TOKEN_KEYS,
  type StorefrontTokenKey,
} from "./storefrontTokens.js";

/** Hex, oklch/rgb/hsl (no ; { } breakouts), or a bare keyword. */
const SAFE_CSS_VALUE =
  /^(#[0-9a-fA-F]{3,8}|oklch\([^;{}]+\)|rgba?\([^;{}]+\)|hsla?\([^;{}]+\)|[a-zA-Z]{3,20})$/;
/** A number, optionally carrying a unit. */
const SAFE_RADIUS = /^(\d{1,3}(?:\.\d+)?)(px|rem|em|%)?$/;
const SAFE_FONT_FAMILY = /^[\w\s,'"-]{1,100}$/;

/** A tenant color that survives the allowlist, else null. */
export function safeThemeColor(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed && SAFE_CSS_VALUE.test(trimmed) ? trimmed : null;
}

/**
 * Corner radius as a CSS length. A unitless number reads as px: only `0` is
 * legal CSS that way, and dropping the rest left `--radius` on the stylesheet
 * default — a tenant who set 40 saw the stock 0.625rem and no way to tell why.
 */
export function safeThemeRadius(value: string | undefined): string | null {
  const match = SAFE_RADIUS.exec(value?.trim() ?? "");
  if (!match) return null;
  return match[2] ? match[0] : `${match[1]}px`;
}

/** A tenant font stack that survives the allowlist, else null. */
export function safeThemeFontFamily(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed && SAFE_FONT_FAMILY.test(trimmed) ? trimmed : null;
}

export interface StorefrontThemeVars {
  /** Every shadcn token, keyed `--<token>`. Always complete. */
  colors: Record<`--${StorefrontTokenKey}`, string>;
  /** Sanitized `--radius`, or null to leave the stylesheet default in place. */
  radius: string | null;
  /**
   * Sanitized font stack, or null. A CSS *property*, not a custom property —
   * callers set `font-family`, so it is kept out of `colors` deliberately.
   */
  fontFamily: string | null;
}

/**
 * The complete, sanitized variable set for a stored theme. Unset tokens resolve
 * to the stock palette, so a consumer always gets a full set and never
 * half-inherits whatever palette it happens to be rendered inside.
 */
export function resolveStorefrontThemeVars(theme?: StorefrontTheme): StorefrontThemeVars {
  const stored = resolveStorefrontTokens(theme?.tokens);
  const colors = {} as Record<`--${StorefrontTokenKey}`, string>;
  for (const key of STOREFRONT_TOKEN_KEYS) {
    colors[`--${key}`] = safeThemeColor(stored[key]) ?? STOREFRONT_TOKEN_DEFAULTS[key];
  }
  return {
    colors,
    radius: safeThemeRadius(theme?.radius),
    fontFamily: safeThemeFontFamily(theme?.fontFamily),
  };
}
